import { ChevronsUp } from "lucide-react";

import { Alert } from "../../../components/Alert/Alert";
import { RangeField } from "../../../components/RangeField/RangeField";
import { Select } from "../../../components/Select/Select";
import { Switch } from "../../../components/Switch/Switch";
import { FieldRow } from "../../projects/config/FieldRow";
import { SectionCard } from "../../projects/config/SectionCard";
import { defaultLeaderboardSim } from "../config/defaults";
import type {
  BotGrowthRule,
  BotsLeaderboardSim,
  BotsScoreMode,
} from "../config/types";
import type { BotsErrors } from "./botsValidation";
import styles from "./BotsSettings.module.css";
import {
  STAT_OPTION,
  scoreModeCell,
  scoreSourceOptions,
  type BoardShape,
} from "./scoreMode";

interface Props {
  rows: BotsLeaderboardSim[];
  onChange: (next: BotsLeaderboardSim[]) => void;
  dirty: boolean;
  /** The project's boards: picked by ext_key, and their operator decides
   *  whether a row's mode is a choice. */
  boards: BoardShape[];
  /** `bots.growth[]` — the stats a board can follow instead of drawing. */
  growth: BotGrowthRule[];
  errors: BotsErrors;
}

/**
 * `bots.leaderboards[]` — simulated scores, off by default and enabled one
 * board at a time.
 *
 * Mockup: t-180/bots-leaderboards-score-from-stat.html. The warning stays
 * verbatim: a bot's score is a real rank, and a real rank is one a living
 * player does not hold.
 */
export function LeaderboardsSection({
  rows,
  onChange,
  dirty,
  boards,
  growth,
  errors,
}: Props) {
  const stale = rows.flatMap((l, i) => {
    const s = errors.scoreStatRows[i];
    return s ? [{ i, board: l.ext_key || `board ${i + 1}`, ...s }] : [];
  });
  return (
    <SectionCard
      id="leaderboards"
      title="Leaderboards"
      apiName="bots.leaderboards[]"
      dirty={dirty}
    >
      <FieldRow
        name="Score simulation"
        apiName="bots.leaderboards[]"
        hint={
          <>
            Off by default and enabled per board by <code>ext_key</code>, never
            globally. <b className={styles.hintStrong}>Score from</b> is either a
            random draw — a whole result or a gain on the bot’s current score —
            or a growing stat from Progression, which the board then mirrors:
            every gain is submitted the moment the bot makes it, and a score that
            disagrees with the stat is corrected to it. Boards whose operator
            fixes the meaning show it read-only.
          </>
        }
        stacked
      >
        <LeaderboardsEditor
          rows={rows}
          boards={boards}
          growth={growth}
          errors={errors}
          onChange={onChange}
        />
        {stale.map((s) => (
          <div key={s.i} className={styles.fieldError}>
            <code>{s.board}</code>: <code>{s.stat}</code> {s.reason}
          </div>
        ))}
        {rows.some((l) => l.enabled) && (
          <Alert
            variant="danger"
            title="Simulated scores take real rewards away from real players"
          >
            A bot's score is a real rank, and a real rank is one a living player
            does not hold. Grants are filtered so no bot ever receives one — on
            an aggregated board they would otherwise fan out to every member,
            bots included, as pending rows nobody claims — but the standings are
            not filtered and cannot honestly be. Enable this only where the
            payout can absorb it.
          </Alert>
        )}
      </FieldRow>
    </SectionCard>
  );
}

function LeaderboardsEditor({
  rows,
  boards,
  growth,
  errors,
  onChange,
}: {
  rows: BotsLeaderboardSim[];
  boards: BoardShape[];
  growth: BotGrowthRule[];
  errors: BotsErrors;
  onChange: (next: BotsLeaderboardSim[]) => void;
}) {
  const keys = boards.map((b) => b.ext_key);
  const growing = growth.some((g) => g.key.trim() !== "");
  const put = (i: number, next: BotsLeaderboardSim) =>
    onChange(rows.map((r, j) => (i === j ? next : r)));
  const patch = (i: number, p: Partial<BotsLeaderboardSim>) =>
    put(i, { ...rows[i], ...p });
  const unused = keys.filter((k) => !rows.some((r) => r.ext_key === k));
  const invalid = (i: number, field: string) =>
    errors.problems.some((p) => p.path === `bots.leaderboards[${i}].${field}`);

  return (
    <div className={styles.gridEditor}>
      <div className={styles.scroller}>
        <div className={`${styles.row} ${styles.lbs} ${styles.head}`}>
          <div>ext_key</div>
          <div className={styles.num}>on</div>
          <div>score from</div>
          <div className={styles.ctr}>submits</div>
          <div className={styles.ctr}>score per submit</div>
          <div />
        </div>
        {rows.map((l, i) => {
          const board = boards.find((b) => b.ext_key === l.ext_key);
          const cell = scoreModeCell(board, growing);
          const follows =
            l.score_stat && cell.kind === "select" ? cell.follows : null;
          return (
            <div key={i} className={`${styles.row} ${styles.lbs}`}>
              <div className={styles.cell}>
                <Select
                  className={styles.rowSelect}
                  value={l.ext_key}
                  onChange={(v) => {
                    // A board where lower wins cannot follow a stat at all.
                    const next = { ...l, ext_key: v };
                    const shape = boards.find((b) => b.ext_key === v);
                    if (scoreModeCell(shape, growing).kind === "fixed") {
                      delete next.score_stat;
                    }
                    put(i, next);
                  }}
                  options={[
                    ...(keys.includes(l.ext_key) || !l.ext_key
                      ? []
                      : [{ value: l.ext_key, label: `${l.ext_key} (removed)` }]),
                    ...keys.map((k) => ({ value: k, label: k, mono: true })),
                  ]}
                  placeholder="leaderboard"
                />
              </div>
              <Switch
                checked={l.enabled}
                onChange={(next) => patch(i, { enabled: next })}
                aria-label={`${l.ext_key || `Board ${i + 1}`} simulation`}
              />
              <SourceCell
                row={l}
                cell={cell}
                growth={growth}
                stale={errors.scoreStatRows[i] !== undefined}
                onChange={(next) => put(i, next)}
              />
              {follows ? (
                <FollowNote stat={l.score_stat ?? ""} follows={follows} />
              ) : (
                <>
                  <RangeField
                    label={`${l.ext_key} submits per day`}
                    unit="a day"
                    value={{
                      min: l.submits_per_day_min,
                      max: l.submits_per_day_max,
                    }}
                    onChange={({ min, max }) =>
                      patch(i, {
                        submits_per_day_min: min,
                        submits_per_day_max: max,
                      })
                    }
                    error={invalid(i, "submits_per_day")}
                  />
                  <RangeField
                    label={`${l.ext_key} score per submit`}
                    unit="pts"
                    value={{
                      min: l.score_per_submit_min,
                      max: l.score_per_submit_max,
                    }}
                    onChange={({ min, max }) =>
                      patch(i, {
                        score_per_submit_min: min,
                        score_per_submit_max: max,
                      })
                    }
                    error={invalid(i, "score_per_submit")}
                  />
                </>
              )}
              <button
                type="button"
                className={styles.rowBtn}
                aria-label={`Remove ${l.ext_key || `board ${i + 1}`}`}
                onClick={() => onChange(rows.filter((_, j) => j !== i))}
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        className={styles.addRow}
        disabled={unused.length === 0}
        onClick={() =>
          onChange([...rows, defaultLeaderboardSim(unused[0] ?? "")])
        }
      >
        {unused.length === 0
          ? "+ Add leaderboard — every board is already listed"
          : "+ Add leaderboard"}
      </button>
    </div>
  );
}

/**
 * The `score from` cell. Choosing a stat only adds `score_stat`, so the row's
 * own numbers and mode are still there when a draw is chosen again.
 */
function SourceCell({
  row,
  cell,
  growth,
  stale,
  onChange,
}: {
  row: BotsLeaderboardSim;
  cell: ReturnType<typeof scoreModeCell>;
  growth: BotGrowthRule[];
  stale: boolean;
  onChange: (next: BotsLeaderboardSim) => void;
}) {
  // A stat saved on a board that cannot follow one: offered back next to
  // Result, so choosing Result clears it.
  if (cell.kind === "fixed" && row.score_stat) {
    const stat = `${STAT_OPTION}${row.score_stat}`;
    return (
      <div className={`${styles.cell} ${styles.selectErr}`}>
        <Select
          className={styles.rowSelect}
          value={stat}
          monospaceValue
          onChange={(v) => {
            if (v === stat) return;
            const next = { ...row };
            delete next.score_stat;
            onChange(next);
          }}
          options={[
            { value: cell.label.toLowerCase(), label: cell.label },
            { value: stat, label: row.score_stat, mono: true },
          ]}
          aria-label={`${row.ext_key} score from`}
        />
      </div>
    );
  }
  if (cell.kind === "fixed") {
    return (
      <div className={styles.modeFixed} title={cell.title}>
        <span>{cell.label}</span>
      </div>
    );
  }
  const options = scoreSourceOptions(cell.draws, growth);
  const value = row.score_stat
    ? `${STAT_OPTION}${row.score_stat}`
    : cell.draws.includes(row.score_mode)
      ? row.score_mode
      : cell.draws[0];
  // A stat that stopped growing stays on the trigger, painted, until replaced.
  if (row.score_stat && !options.some((o) => o.value === value)) {
    options.push({ value, label: row.score_stat, mono: true });
  }
  return (
    <div className={`${styles.cell} ${stale ? styles.selectErr : ""}`}>
      <Select
        className={styles.rowSelect}
        value={value}
        monospaceValue={!!row.score_stat}
        onChange={(v) => {
          const next = { ...row };
          if (v.startsWith(STAT_OPTION)) {
            next.score_stat = v.slice(STAT_OPTION.length);
          } else {
            delete next.score_stat;
            // An increment board has one draw, and it is not a stored choice.
            if (cell.draws.length > 1) next.score_mode = v as BotsScoreMode;
          }
          onChange(next);
        }}
        options={options}
        aria-label={`${row.ext_key} score from`}
      />
    </div>
  );
}

/** The submits and score cells of a board that follows a stat, as one note. */
function FollowNote({
  stat,
  follows,
}: {
  stat: string;
  follows: "value" | "gain";
}) {
  return (
    <div
      className={styles.follow}
      title={
        follows === "gain"
          ? "An increment board adds what it receives, so each gain itself is submitted"
          : `Each time a bot's ${stat} grows, its new ${stat} is submitted here`
      }
    >
      <ChevronsUp size={14} aria-hidden />
      {follows === "gain" ? (
        <>
          the gain of <code>{stat}</code>, as it happens
        </>
      ) : (
        <>
          new value of <code>{stat}</code> on every gain
        </>
      )}
    </div>
  );
}
